import { useNavigate, useParams } from "react-router-dom";
import CloseAppBar from "../../../components/atoms/appbar/CloseAppbar";
import Button from "../../../components/atoms/button";
import SelectBox from "../../../components/atoms/select";
import { useModalStore } from "../../../lib/store/useModalStore";
import { cn } from "../../../lib/utils/cn";
import { vw } from "../../../lib/utils/responsiveUtils";
import * as React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { formatKoDayDate } from "../../../lib/utils/dateUtils";
import { useGetResveDetail } from "../../../@apis/hooks/useResve";
import { staticURL } from "../../../lib/utils/imageUtils";
import { usePayCancel } from "../../../@apis/hooks/usePay";
import type { CancelRequest } from "../../../@apis/dto/request/payment.request";
import { putPayCancel } from "../../../@apis/v1/payment.service";
import { getQueryClient } from "../../../@apis/queryClient";

export default function ReservationCancel() {
  const { openModal } = useModalStore();
  const navigate = useNavigate();
  const { id } = useParams();

  const [reason, setReason] = React.useState("");

  const { data: resveCancelResponse } = useQuery({
    ...useGetResveDetail(id!),
  });
  const resveCancelInfo = resveCancelResponse?.data?.resveInfo;
  
  const { data: payCancelResponse } = useQuery({
    ...usePayCancel({
      golfparkCode: resveCancelInfo?.golfParkCode || "",
      resveId: Number(id)
    }),
    enabled: !!resveCancelInfo?.golfParkCode && !!id,
  })
  const cancel = payCancelResponse?.data;

  const payCancelMutation = useMutation({
    mutationFn: (form: CancelRequest) => putPayCancel(form),
    onSuccess: (res) => {
      if (res.success) {
        getQueryClient().removeQueries({ queryKey: ["resve-list"] });
        getQueryClient().removeQueries({ queryKey: ["resve-cancel-list"] });

        navigate(-2);
      } else {
        alert(res.error?.message);
      }
    },
  });

  if (!resveCancelInfo) return <p>로딩중...</p>;
  const myMember = resveCancelInfo.resveTimeMemberList?.[0];

  const handleCancel = () => {
    if (!id) return;

    const form: CancelRequest = {
      resveId: Number(id),
      golfparkCode: resveCancelInfo?.golfParkCode ?? "",
    };

    payCancelMutation.mutate(form);
  };

  const formattedCardNumber = (cardNo: string) => {
    if (typeof cardNo !== 'string' || cardNo.length !== 16) {
      return null;
    }

    const chunks = cardNo.match(/.{1,4}/g);
    if (!chunks) {
      return null;
    }
    const formatted = chunks.join("-")
    return formatted.slice(0, -1) + "*"
  }
  
  return (
    <div className="flex flex-col min-h-screen">
      <CloseAppBar title="예약 취소" onBack={() => (navigate(-1))}/>

      <div className="px-[15px] mb-[100px]">
        <div>
          {/* 날짜 영역 */}
          <div className="rounded-[15px] bg-[#f5f5f5] p-[20px] flex items-center" style={{ height: vw(60) }}>
            <p className="font-bold text-[rgb(34,34,34)]" style={{ fontSize: vw(16) }}>
              {formatKoDayDate(resveCancelInfo?.baseDate)} {resveCancelInfo?.startTime}
            </p>
          </div>

          <div className="mx-auto" style={{ width: vw(315), height: "1px", backgroundImage: "repeating-linear-gradient(to right,#e6e6e6 0 3px,transparent 6px 5px)", borderRadius: "2px", }} />

          {/* 내용 영역 */}
          <div className="rounded-[15px] bg-[#f5f5f5] p-[20px] flex items-center" style={{ height: vw(124) }}>
            <div className="flex items-center gap-[19px]">
              <img
                className="rounded-[10px] bg-white"
                style={{ width: vw(83), height: vw(83) }}
                src={resveCancelInfo?.golfParkImageUrl ? staticURL(resveCancelInfo?.golfParkImageUrl) : "/images/img-login-logo.webp"}
                alt="골프장 이미지"
              />
              <div className="flex flex-col justify-center">
                <p className="text-[#222] font-bold" style={{ fontSize: vw(20) }}>
                  {resveCancelInfo?.golfParkName}
                </p>
                <div className="mt-[18px] pl-[15px] border-l-[2px] border-[#e6e6e6]">
                  <p
                    className="text-[14px] text-[#333] font-[500]"
                    style={{ fontSize: vw(14) }}
                  >
                    {resveCancelInfo?.golfCourseName}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-[15px]">
          <div className="mt-[15px] rounded-[15px] bg-[#f5f5f5] p-[20px] flex items-center justify-between" style={{ height: vw(60) }}>
            <p className="font-bold text-[#222]" style={{ fontSize: vw(16) }}>
              라운드 비용
            </p>
            <p className="font-bold text-[#222]" style={{ fontSize: vw(20) }}>
              {myMember?.payMemberNum}인 {(myMember?.amt)?.toLocaleString()}원
            </p>
          </div>

          <div className="mx-auto" style={{ width: vw(315), height: "1px", backgroundImage: "repeating-linear-gradient(to right,#e6e6e6 0 3px,transparent 6px 5px)", borderRadius: "2px", }} />

          <div className="rounded-[15px] bg-[#f5f5f5] p-[20px] flex flex-col justify-center" style={{ height: vw(106) }}>
            <div className="flex items-center justify-between">
              <p className="text-[#222] font-bold" style={{ fontSize: vw(16) }}>결제 정보</p>
              <p className="text-[#222] font-bold" style={{ fontSize: vw(20) }}> {(cancel?.originalAmt)?.toLocaleString()}원</p>
            </div>

            <div className="mt-[15px] pl-[15px] border-l-[2px] border-[#e6e6e6]">
              <div className="flex justify-between">
                <p className="text-[#888] font-[500]" style={{ fontSize: vw(14) }}>결제 수단</p>
                <p className="text-[#222] font-[500]" style={{ fontSize: vw(14) }}>{myMember?.payMethod === "CASH" ? "현장결제" : myMember?.payMethod === "CARD" ? `${myMember?.cardName ?? ""}카드 ${formattedCardNumber(myMember?.cardNo || "")}` : ""}</p>
              </div>
              <div className="flex justify-between mt-[6px]">
                <p className="text-[#888] font-[500]" style={{ fontSize: vw(14) }}>결제 금액</p>
                <p className="text-[#222] font-[500]" style={{ fontSize: vw(14) }}>{(myMember?.amt)?.toLocaleString()}원</p>
              </div>
            </div>
          </div>

          <div className="mx-auto" style={{ width: vw(315), height: "1px", backgroundImage: "repeating-linear-gradient(to right,#e6e6e6 0 3px,transparent 6px 5px)", borderRadius: "2px", }} />

          <div className="rounded-[15px] bg-[#f5f5f5] p-[20px] flex flex-col justify-center" style={{ height: vw(194) }}>
            <div className="flex items-center justify-between">
              <p className="text-[#222] font-bold" style={{ fontSize: vw(16) }}>환불 예정 정보</p>
              <p className="text-[#e5002b] font-bold" style={{ fontSize: vw(20) }}>{(cancel?.refundAmt)?.toLocaleString()}원</p>
            </div>

            <div className="mt-[15px] pl-[15px] border-l-[2px] border-[#e6e6e6]">
              <div className="flex justify-between">
                <p className="text-[#888] font-[500]" style={{ fontSize: vw(14) }}>결제 수단</p>
                <p className="text-[#222] font-[500]" style={{ fontSize: vw(14) }}>{myMember?.payMethod === "CASH" ? "현장결제" : myMember?.payMethod === "CARD" ? `${myMember?.cardName ?? ""}카드 ${formattedCardNumber(myMember?.cardNo || "")}` : ""}</p>
              </div>
              <div className="flex justify-between mt-[6px]">
                <p className="text-[#888] font-[500]" style={{ fontSize: vw(14) }}>결제 금액</p>
                <p className="text-[#222] font-[500]" style={{ fontSize: vw(14) }}>{(myMember?.amt)?.toLocaleString()}원</p>
              </div>
              <div className="flex justify-between mt-[6px]">
                <p className="text-[#888] font-[500]" style={{ fontSize: vw(14) }}>취소 수수료</p>
                <p className="text-[#222] font-[500]" style={{ fontSize: vw(14) }}>{((myMember?.amt ?? 0) - (cancel?.refundAmt ?? 0)).toLocaleString()}원</p>
              </div>
            </div>
            <div className="mt-[14px] bg-[#fff] rounded-[15px] text-[#888] font-[500] flex items-center justify-center" style={{ height: vw(50), fontSize: vw(12) }}>
              환불은 영업일 기준 3일~10일정도 소요될 수 있습니다.
            </div>
          </div>
        </div>
        <div className="mt-[25px] space-y-5">
          <p className="font-bold" style={{ fontSize: vw(16) }}>취소 사유</p>
          <div className="space-y-[20px]">
            <SelectBox
              className={reason === "" ? "text-[#ccc]" : "text-[#222]"}
              style={{ fontSize: vw(14) }}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              options={[
                { value: "", label: "예약 취소 사유를 선택해주세요."},
                { value: "1", label: "개인 사정으로 인한 취소"},
              ]}
            />
          </div>
        </div>
      </div>

      <div className={cn("bg-white p-[15px] fixed max-w-[440px] w-full bottom-0 flex-row-between z-50",)}>
        <Button
          type="button"
          className="w-full font-bold h-[50px]"
          variant={"black"}
          label="예약 취소"
          onClick={() => {
            if (!cancel) return;

            if (cancel.refundAmt === 0) {
              openModal("ALERT_DIALOG", {
                title: "예약 취소 불가",
                content: `예약일로 부터 ${cancel.beforeResveDate}일 전에는 예약 취소가 불가능 합니다.`,
              })
            }
            
            openModal("CONFIRM_DIALOG", {
              title: "예약 취소",
              content: "예약을 취소하시겠습니까?",
              explain: "정책에 따라 환불이 진행되며, 영업일 기준 3~10일 소요될 수 있습니다.",
              onClick: handleCancel,    
            })
          }}
        />
      </div> 
      
    </div>
  )
}